import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useUI } from '../context/UIContext';
import CardImage from './CardImage';
import StartChatSheet from './trade/StartChatSheet';
import { requestChat } from '../services/tradeRequest';

// Icons
const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const ClearIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="m15 9-6 6" />
    <path d="m9 9 6 6" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const FilterIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Search Result Card Component
const SearchResultCard = ({
  card,
  owner,
  viewMode,
  onCardPress,
  onOwnerPress,
  onTrade,
  onNotInterested,
  tradeRequested,
  niCountdown,
  isFollowing,
  distance
}) => {
  const canTrade = card.trading_status === 'TRADE';

  return (
    <div style={{
      ...styles.resultCard,
      opacity: niCountdown !== null ? 0.5 : 1,
    }}>
      {/* Card Image */}
      <div style={styles.resultImageContainer} onClick={onCardPress}>
        <CardImage
          digitalImage={card.digital_image_url}
          userPhoto={card.original_image_url}
          name={card.name}
          number={card.set_number}
          setName={card.set_name}
          preferPhoto={viewMode === 'photo'}
          imgStyle={styles.resultImage}
        />
      </div>

      {/* Card Info */}
      <div style={styles.resultInfo}>
        <span style={styles.resultName}>{card.name}</span>
        <span style={styles.resultSet}>{card.set_name}</span>
        <div style={styles.resultMeta}>
          {card.set_number && (
            <span style={styles.resultNumber}>#{card.set_number}</span>
          )}
          {card.grade && (
            <span style={styles.resultGrade}>{card.grade}</span>
          )}
          <span style={{
            ...styles.resultStatus,
            backgroundColor: canTrade ? 'rgba(34, 197, 94, 0.2)' : 'rgba(107, 114, 128, 0.2)',
            color: canTrade ? '#22c55e' : '#6b7280',
          }}>
            {canTrade ? 'Trade' : 'PC'}
          </span>
        </div>

        {/* Owner with following badge */}
        <div style={styles.ownerRow}>
          <button style={styles.ownerButton} onClick={onOwnerPress}>
            <span style={styles.ownerText}>@{owner?.username || 'unknown'}</span>
          </button>
          {isFollowing && (
            <span style={styles.followingBadge}>
              <CheckIcon />
              Following
            </span>
          )}
        </div>

        {/* Distance */}
        {distance !== null && (
          <div style={styles.distanceRow}>
            <MapPinIcon />
            <span style={styles.distanceText}>{distance} mi away</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={styles.resultActions}>
        {canTrade && (
          <button
            style={{
              ...styles.tradeButton,
              ...(tradeRequested ? styles.tradeButtonRequested : {}),
            }}
            onClick={onTrade}
          >
            {tradeRequested ? 'Asked' : 'Chat'}
          </button>
        )}
        <button
          style={styles.niButton}
          onClick={onNotInterested}
        >
          {niCountdown !== null ? `Undo (${niCountdown}s)` : 'NI'}
        </button>
      </div>
    </div>
  );
};

// NI Card Component (grid view)
const NICard = ({ card, owner, viewMode, onRemove }) => {
  return (
    <div style={styles.niCard}>
      <div style={styles.niImageContainer}>
        <CardImage
          digitalImage={card.digital_image_url}
          userPhoto={card.original_image_url}
          name={card.name}
          number={card.set_number}
          setName={card.set_name}
          preferPhoto={viewMode === 'photo'}
          imgStyle={styles.niImage}
        />
      </div>
      <div style={styles.niInfo}>
        <span style={styles.niName}>{card.name}</span>
        <span style={styles.niSet}>{card.set_name}</span>
        <span style={styles.niOwner}>@{owner?.username || 'unknown'}</span>
      </div>
      <button style={styles.removeNiButton} onClick={onRemove}>
        Remove
      </button>
    </div>
  );
};

// Distance range options
const DISTANCE_OPTIONS = [
  { value: 10, label: '10 mi' },
  { value: 25, label: '25 mi' },
  { value: 50, label: '50 mi' },
  { value: 100, label: '100 mi' },
  { value: 250, label: '250 mi' },
  { value: null, label: 'Any' },
];

const Search = ({ isOpen, onClose, currentUserId, viewMode }) => {
  const navigate = useNavigate();
  const { setHideNav, searchQuery, searchResults, setSearchQuery, setSearchResults, searchFilters, setSearchFilters } = useUI();

  // Use context for persistent state
  const query = searchQuery;
  const setQuery = (val) => {
    setSearchQuery(val);
    // Clear exact filters when user manually types
    if (searchFilters) {
      setSearchFilters(null);
    }
  };
  const results = searchResults;
  const setResults = setSearchResults;
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('search'); // 'search' or 'ni'
  const [niCards, setNiCards] = useState([]);
  const [niLoading, setNiLoading] = useState(false);
  const [tradeRequests, setTradeRequests] = useState({}); // { ownerId: true }
  const [askSheet, setAskSheet] = useState(null);
  const [asking, setAsking] = useState(false);
  const [niCountdowns, setNiCountdowns] = useState({}); // { cardId: countdown }
  const [notInterestedIds, setNotInterestedIds] = useState(new Set());
  const [followingIds, setFollowingIds] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [tradeOnly, setTradeOnly] = useState(false);
  const [distanceFilter, setDistanceFilter] = useState(null); // null = any distance

  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);
  const countdownRefs = useRef({});

  // Hide bottom nav when search is open
  useEffect(() => {
    setHideNav(isOpen);
    return () => setHideNav(false);
  }, [isOpen, setHideNav]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Clear local state when closed (but NOT query/results - those persist in context)
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('search');
      setShowFilters(false);
      // Clear all countdowns
      Object.values(countdownRefs.current).forEach(clearInterval);
      countdownRefs.current = {};
      setNiCountdowns({});
    }
  }, [isOpen]);

  // Load following IDs (only once)
  useEffect(() => {
    const loadFollowingIds = async () => {
      if (!currentUserId || followingIds.size > 0) return; // Skip if already loaded

      const { data } = await supabase
        .from('user_follows')
        .select('following_id')
        .eq('follower_id', currentUserId);

      if (data) {
        setFollowingIds(new Set(data.map(d => d.following_id)));
      }
    };

    if (isOpen) {
      loadFollowingIds();
    }
  }, [isOpen, currentUserId, followingIds.size]);

  // Load not interested card IDs (only once)
  useEffect(() => {
    const loadNotInterestedIds = async () => {
      if (!currentUserId || notInterestedIds.size > 0) return; // Skip if already loaded

      const { data } = await supabase
        .from('not_interested_cards')
        .select('card_id')
        .eq('user_id', currentUserId);

      if (data) {
        setNotInterestedIds(new Set(data.map(d => d.card_id)));
      }
    };

    if (isOpen) {
      loadNotInterestedIds();
    }
  }, [isOpen, currentUserId, notInterestedIds.size]);

  // Who you already have something open with.
  //
  // Keyed by owner, not by card, because a request is about a person: once one
  // is open, every card they own says so rather than only the one that was
  // tapped. This also read `requested_card_id`, a column that does not exist,
  // so PostgREST rejected the whole select and nothing ever loaded.
  useEffect(() => {
    const loadTradeRequests = async () => {
      if (!currentUserId || Object.keys(tradeRequests).length > 0) return;

      const { data, error } = await supabase
        .from('trade_requests')
        .select('card_owner_id')
        .eq('requester_id', currentUserId)
        .in('status', ['pending', 'accepted']);

      if (error) {
        console.error('[Search] Could not load open requests:', error);
        return;
      }
      if (data) {
        const requests = {};
        data.forEach((d) => { requests[d.card_owner_id] = true; });
        setTradeRequests(requests);
      }
    };

    if (isOpen) {
      loadTradeRequests();
    }
  }, [isOpen, currentUserId, tradeRequests]);

  // Search function
  const searchCards = useCallback(async (searchQuery) => {
    if (!searchQuery.trim() || !currentUserId) {
      setResults([]);
      return;
    }

    setLoading(true);

    try {
      // Build query based on whether we have exact filters or regular search
      let query = supabase
        .from('cards')
        .select(`
          id,
          name,
          set_name,
          set_number,
          grade,
          digital_image_url,
          original_image_url,
          trading_status,
          user_id
        `)
        .eq('traded', false);

      if (searchFilters && searchFilters.name) {
        // Exact card search - use AND logic
        query = query.ilike('name', `%${searchFilters.name}%`);
        if (searchFilters.set_name) {
          query = query.ilike('set_name', `%${searchFilters.set_name}%`);
        }
        if (searchFilters.set_number) {
          query = query.ilike('set_number', `%${searchFilters.set_number}%`);
        }
      } else {
        // Regular search - use OR logic
        query = query.or(`name.ilike.%${searchQuery}%,set_name.ilike.%${searchQuery}%,set_number.ilike.%${searchQuery}%`);
      }

      const { data, error } = await query.is('deleted_at', null).limit(100);

      if (error) {
        console.error('Search error:', error);
        setResults([]);
        return;
      }

      // Filter out own cards
      const filteredData = data?.filter(card => card.user_id !== currentUserId) || [];

      // Get unique user IDs
      const userIds = [...new Set(filteredData.map(c => c.user_id).filter(Boolean))];

      let usersMap = {};
      if (userIds.length > 0) {
        // public_profiles instead of users: the owner's coordinates are no
        // longer sent to the client, so distance_mi arrives already computed
        // from this viewer's own stored location.
        const { data: usersData } = await supabase
          .from('public_profiles')
          .select('id, username, avatar_url, location_sharing_enabled, distance_mi')
          .in('id', userIds);

        usersData?.forEach(u => { usersMap[u.id] = u; });
      }

      // Attach user info and calculate distances
      let resultsWithUsers = filteredData.map(card => {
        const owner = usersMap[card.user_id] || null;
        let distance = null;

        // The owner opting in is still the gate, exactly as before. What
        // changed is only where the haversine ran.
        if (owner?.location_sharing_enabled && owner?.distance_mi != null) {
          distance = owner.distance_mi;
        }

        return {
          ...card,
          users: owner,
          distance
        };
      });

      // Apply distance filter
      if (distanceFilter !== null) {
        resultsWithUsers = resultsWithUsers.filter(card =>
          card.distance !== null && card.distance <= distanceFilter
        );
      }

      // Sort by distance (closest first) if we have location, otherwise by name
      resultsWithUsers.sort((a, b) => {
        if (a.distance !== null && b.distance !== null) {
          return a.distance - b.distance;
        }
        if (a.distance !== null) return -1;
        if (b.distance !== null) return 1;
        return 0;
      });

      // Apply trade-only filter
      if (tradeOnly) {
        resultsWithUsers = resultsWithUsers.filter(card => card.trading_status === 'TRADE');
      }

      // Filter out not interested cards
      const filtered = resultsWithUsers.filter(card => !notInterestedIds.has(card.id));
      setResults(filtered.slice(0, 50)); // Limit to 50 results
    } catch (err) {
      console.error('Search error:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [currentUserId, notInterestedIds, distanceFilter, tradeOnly, searchFilters]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchCards(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, searchCards]);

  // Load NI cards
  const loadNiCards = useCallback(async () => {
    if (!currentUserId) return;

    setNiLoading(true);

    try {
      const { data, error } = await supabase
        .from('not_interested_cards')
        .select('card_id')
        .eq('user_id', currentUserId);

      if (error) {
        console.error('NI load error:', error);
        return;
      }

      if (!data || data.length === 0) {
        setNiCards([]);
        return;
      }

      const cardIds = data.map(d => d.card_id);

      // Fetch the actual cards
      const { data: cardsData } = await supabase
        .from('cards')
        .select('id, name, set_name, set_number, grade, digital_image_url, original_image_url, trading_status, user_id')
        .in('id', cardIds)
        .is('deleted_at', null);

      // Get user info
      const userIds = [...new Set(cardsData?.map(c => c.user_id).filter(Boolean) || [])];
      let usersMap = {};
      if (userIds.length > 0) {
        const { data: usersData } = await supabase
          .from('public_profiles')
          .select('id, username, avatar_url')
          .in('id', userIds);
        usersData?.forEach(u => { usersMap[u.id] = u; });
      }

      const cards = cardsData?.map(card => ({
        ...card,
        users: usersMap[card.user_id] || null
      })) || [];

      setNiCards(cards);
    } catch (err) {
      console.error('NI load error:', err);
    } finally {
      setNiLoading(false);
    }
  }, [currentUserId]);

  // Load NI cards when tab changes
  useEffect(() => {
    if (activeTab === 'ni') {
      loadNiCards();
    }
  }, [activeTab, loadNiCards]);

  // Handle card press - just navigate, don't close search
  const handleCardPress = (cardId) => {
    navigate(`/card/${cardId}`);
  };

  // Handle owner press - just navigate, don't close search
  const handleOwnerPress = (ownerId) => {
    navigate(`/user/${ownerId}/collection`);
  };

  // Tapping a card here asks to meet whoever owns it. The card is the door.
  //
  // This used to set local state and write nothing at all - the comment said
  // "for now, just mark as requested" - so the button looked like it worked
  // and no request ever existed.
  const handleTrade = (card) => {
    const owner = card?.users;
    if (!owner?.id || owner.id === currentUserId) return;
    if (tradeRequests[owner.id]) return;
    setAskSheet({
      ownerId: owner.id,
      ownerName: owner.username || 'this trader',
      avatarUrl: owner.avatar_url,
      fromCardId: card.id,
    });
  };

  const confirmAsk = async () => {
    if (!askSheet || asking) return;
    setAsking(true);
    try {
      const { error } = await requestChat({
        requesterId: currentUserId,
        ownerId: askSheet.ownerId,
        fromCardId: askSheet.fromCardId,
      });
      if (error) {
        console.error('[Search] Could not start chat:', error);
        return;
      }
      setTradeRequests((prev) => ({ ...prev, [askSheet.ownerId]: true }));
      setAskSheet(null);
    } finally {
      setAsking(false);
    }
  };

  // Handle not interested
  const handleNotInterested = (cardId) => {
    if (niCountdowns[cardId] !== undefined) {
      // Undo - cancel countdown
      if (countdownRefs.current[cardId]) {
        clearInterval(countdownRefs.current[cardId]);
        delete countdownRefs.current[cardId];
      }
      setNiCountdowns(prev => {
        const updated = { ...prev };
        delete updated[cardId];
        return updated;
      });
      return;
    }

    // Start countdown
    setNiCountdowns(prev => ({ ...prev, [cardId]: 2 }));

    countdownRefs.current[cardId] = setInterval(() => {
      setNiCountdowns(prev => {
        const current = prev[cardId];
        if (current === undefined) return prev;

        if (current <= 1) {
          // Time's up - mark as not interested
          clearInterval(countdownRefs.current[cardId]);
          delete countdownRefs.current[cardId];

          // Save to database
          supabase
            .from('not_interested_cards')
            .insert({ user_id: currentUserId, card_id: cardId })
            .then(() => {
              // Remove from results
              setResults(r => r.filter(c => c.id !== cardId));
              setNotInterestedIds(ids => new Set([...ids, cardId]));
            });

          const updated = { ...prev };
          delete updated[cardId];
          return updated;
        }

        return { ...prev, [cardId]: current - 1 };
      });
    }, 1000);
  };

  // Handle remove from NI
  const handleRemoveNi = async (cardId) => {
    await supabase
      .from('not_interested_cards')
      .delete()
      .eq('user_id', currentUserId)
      .eq('card_id', cardId);

    setNiCards(prev => prev.filter(c => c.id !== cardId));
    setNotInterestedIds(ids => {
      const updated = new Set(ids);
      updated.delete(cardId);
      return updated;
    });
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.searchInputContainer}>
            <SearchIcon />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search cards..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={styles.searchInput}
            />
            {query && (
              <button style={styles.clearButton} onClick={() => setQuery('')}>
                <ClearIcon />
              </button>
            )}
          </div>
          <button
            style={{
              ...styles.filterButton,
              ...(showFilters || tradeOnly || distanceFilter ? { backgroundColor: 'rgba(14, 165, 233, 0.15)', borderColor: '#0ea5e9' } : {}),
            }}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FilterIcon />
          </button>
          <button style={styles.closeButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div style={{
            display: 'flex',
            gap: 8,
            padding: '10px 16px',
            borderBottom: '1px solid #2a2a2a',
            flexWrap: 'wrap',
          }}>
            <button
              style={{
                padding: '6px 14px',
                borderRadius: 20,
                border: tradeOnly ? '1px solid #0ea5e9' : '1px solid #2a2a2a',
                backgroundColor: tradeOnly ? 'rgba(14, 165, 233, 0.15)' : '#1a1a1a',
                color: tradeOnly ? '#0ea5e9' : '#94a3b8',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
              onClick={() => setTradeOnly(!tradeOnly)}
            >
              Trade Only
            </button>
            {[5, 25, 50, 100].map(mi => (
              <button
                key={mi}
                style={{
                  padding: '6px 14px',
                  borderRadius: 20,
                  border: distanceFilter === mi ? '1px solid #0ea5e9' : '1px solid #2a2a2a',
                  backgroundColor: distanceFilter === mi ? 'rgba(14, 165, 233, 0.15)' : '#1a1a1a',
                  color: distanceFilter === mi ? '#0ea5e9' : '#94a3b8',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                onClick={() => setDistanceFilter(distanceFilter === mi ? null : mi)}
              >
                {`< ${mi} mi`}
              </button>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div style={styles.tabs}>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'search' ? styles.tabActive : {}),
            }}
            onClick={() => setActiveTab('search')}
          >
            Search Results
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'ni' ? styles.tabActive : {}),
            }}
            onClick={() => setActiveTab('ni')}
          >
            Not Interested
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {activeTab === 'search' ? (
            // Search Results - don't show loading if we already have results
            loading && results.length === 0 ? (
              <div style={styles.loadingContainer}>
                <div style={styles.spinner} />
                <span style={styles.loadingText}>Searching...</span>
              </div>
            ) : !query.trim() ? (
              <div style={styles.emptyState}>
                <SearchIcon />
                <span style={styles.emptyText}>Start typing to search</span>
                <span style={styles.emptySubtext}>Search by card name, set, or number</span>
              </div>
            ) : results.length === 0 ? (
              <div style={styles.emptyState}>
                <span style={styles.emptyText}>No cards found</span>
                <span style={styles.emptySubtext}>
                  {distanceFilter !== null
                    ? `Try increasing the distance or changing your search`
                    : `Try a different search term`}
                </span>
              </div>
            ) : (
              <div style={styles.resultsList}>
                {results.map(card => (
                  <SearchResultCard
                    key={card.id}
                    card={card}
                    owner={card.users}
                    viewMode={viewMode}
                    onCardPress={() => handleCardPress(card.id)}
                    onOwnerPress={() => handleOwnerPress(card.users?.id)}
                    onTrade={() => handleTrade(card)}
                    onNotInterested={() => handleNotInterested(card.id)}
                    tradeRequested={!!tradeRequests[card.users?.id]}
                    niCountdown={niCountdowns[card.id] ?? null}
                    isFollowing={followingIds.has(card.users?.id)}
                    distance={card.distance}
                  />
                ))}
              </div>
            )
          ) : (
            // Not Interested Tab
            niLoading ? (
              <div style={styles.loadingContainer}>
                <div style={styles.spinner} />
                <span style={styles.loadingText}>Loading...</span>
              </div>
            ) : niCards.length === 0 ? (
              <div style={styles.emptyState}>
                <span style={styles.emptyText}>No cards here</span>
                <span style={styles.emptySubtext}>Cards you mark as "Not Interested" will appear here</span>
              </div>
            ) : (
              <div style={styles.niGrid}>
                {niCards.map(card => (
                  <NICard
                    key={card.id}
                    card={card}
                    owner={card.users}
                    viewMode={viewMode}
                    onRemove={() => handleRemoveNi(card.id)}
                  />
                ))}
              </div>
            )
          )}
        </div>
      </div>

      {/* Asking to meet whoever owns the card that was tapped. */}
      <StartChatSheet
        isOpen={!!askSheet}
        username={askSheet?.ownerName}
        avatarUrl={askSheet?.avatarUrl}
        sending={asking}
        onStart={confirmAsk}
        onClose={() => setAskSheet(null)}
      />
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--bg)',
    zIndex: 2000,
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 'var(--safe-area-top, 0px)',
    paddingBottom: 'var(--safe-area-bottom, 0px)',
    paddingLeft: 'var(--safe-area-left, 0px)',
    paddingRight: 'var(--safe-area-right, 0px)',
    overflow: 'hidden',
    overscrollBehavior: 'none',
    touchAction: 'none',
  },
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '16px',
    borderBottom: '1px solid var(--border-subtle)',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    backgroundColor: 'var(--bg)',
    zIndex: 10,
  },
  searchInputContainer: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: 'var(--bg-sheet)',
    borderRadius: '12px',
    padding: '12px 16px',
    border: '1px solid var(--border)',
    color: 'var(--text-secondary)',
    overflow: 'hidden',
  },
  searchInput: {
    flex: 1,
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    color: 'var(--text)',
    fontSize: '16px',
    fontFamily: 'inherit',
  },
  clearButton: {
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  filterButton: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
    flexShrink: 0,
    transition: 'all 0.15s ease',
  },
  filterButtonActive: {
    backgroundColor: 'var(--accent-bg)',
    borderColor: 'var(--accent)',
    color: 'var(--accent)',
  },
  closeButton: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text)',
    flexShrink: 0,
  },
  filterPanel: {
    padding: '12px 16px',
    backgroundColor: 'var(--bg-sheet)',
    borderBottom: '1px solid var(--border-subtle)',
  },
  filterSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  filterLabel: {
    color: 'var(--text-secondary)',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  filterOptions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  filterOption: {
    padding: '8px 14px',
    borderRadius: '20px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    color: 'var(--text-secondary)',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  filterOptionActive: {
    backgroundColor: 'var(--accent-bg)',
    borderColor: 'var(--accent)',
    color: 'var(--accent)',
  },
  filterWarning: {
    color: 'var(--warning)',
    fontSize: '12px',
    marginTop: '4px',
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid var(--border-subtle)',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    backgroundColor: 'var(--bg)',
    zIndex: 9,
  },
  tab: {
    flex: 1,
    padding: '14px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: 'var(--text-secondary)',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  tabActive: {
    color: 'var(--accent)',
    borderBottomColor: 'var(--accent)',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 0,
    WebkitOverflowScrolling: 'touch',
    touchAction: 'pan-y',
    overscrollBehavior: 'contain',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    width: '100%',
    gap: '12px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid var(--border)',
    borderTopColor: 'var(--accent)',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    color: 'var(--text-secondary)',
    fontSize: '14px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    gap: '8px',
    color: 'var(--text-secondary)',
  },
  emptyText: {
    color: 'var(--text-muted)',
    fontSize: '16px',
    fontWeight: '500',
  },
  emptySubtext: {
    color: 'var(--text-secondary)',
    fontSize: '14px',
    textAlign: 'center',
    padding: '0 20px',
  },
  resultsList: {
    padding: '16px 16px var(--nav-clearance) 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  // Search Result Card
  resultCard: {
    display: 'flex',
    gap: '12px',
    padding: '12px',
    backgroundColor: 'var(--bg-sheet)',
    borderRadius: '12px',
    border: '1px solid var(--border)',
    transition: 'opacity 0.3s ease',
  },
  resultImageContainer: {
    width: '80px',
    height: '112px',
    borderRadius: '8px',
    overflow: 'hidden',
    flexShrink: 0,
    cursor: 'pointer',
    backgroundColor: 'var(--bg-card)',
  },
  resultImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  resultImagePlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--border)',
  },
  placeholderText: {
    color: 'var(--text-secondary)',
    fontSize: '20px',
    fontWeight: '600',
  },
  resultInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    minWidth: 0,
  },
  resultName: {
    color: 'var(--text)',
    fontSize: '15px',
    fontWeight: '600',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  resultSet: {
    color: 'var(--text-muted)',
    fontSize: '13px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  resultMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap',
    marginTop: '2px',
  },
  resultNumber: {
    color: 'var(--accent)',
    fontSize: '12px',
    fontWeight: '600',
  },
  resultGrade: {
    padding: '2px 8px',
    backgroundColor: 'var(--accent-bg)',
    color: 'var(--accent)',
    fontSize: '11px',
    fontWeight: '600',
    borderRadius: '10px',
  },
  resultStatus: {
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: '600',
    borderRadius: '10px',
  },
  ownerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '2px',
  },
  ownerButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
  },
  ownerText: {
    color: 'var(--text-secondary)',
    fontSize: '12px',
  },
  followingBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    color: 'var(--text-secondary)',
    fontSize: '10px',
    fontWeight: '500',
  },
  distanceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: 'var(--text-secondary)',
    marginTop: '2px',
  },
  distanceText: {
    fontSize: '11px',
  },
  resultActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    justifyContent: 'center',
  },
  tradeButton: {
    padding: '8px 14px',
    borderRadius: '8px',
    backgroundColor: 'var(--error-bg)',
    border: '1px solid var(--error)',
    color: 'var(--error)',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    whiteSpace: 'nowrap',
  },
  tradeButtonRequested: {
    backgroundColor: 'var(--error)',
    color: '#ffffff',
  },
  niButton: {
    padding: '8px 14px',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border-hover)',
    color: 'var(--text-muted)',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    whiteSpace: 'nowrap',
  },
  // NI Grid
  niGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    padding: '16px 16px var(--nav-clearance) 16px',
  },
  niCard: {
    backgroundColor: 'var(--bg-sheet)',
    borderRadius: '12px',
    border: '1px solid var(--border)',
    overflow: 'hidden',
  },
  niImageContainer: {
    width: '100%',
    aspectRatio: '0.714',
    backgroundColor: 'var(--bg-card)',
  },
  niImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  niImagePlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--border)',
  },
  niInfo: {
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  niName: {
    color: 'var(--text)',
    fontSize: '13px',
    fontWeight: '600',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  niSet: {
    color: 'var(--text-secondary)',
    fontSize: '11px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  niOwner: {
    color: 'var(--text-secondary)',
    fontSize: '11px',
  },
  removeNiButton: {
    width: '100%',
    padding: '10px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: 'none',
    borderTop: '1px solid var(--border)',
    color: 'var(--error)',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default Search;
